// POS-001 client: item lookup (search / barcode), counter sales. Shapes mirror
// packages/domain/pos/src/http.ts (presentSale) and the lookup handler.
import { useInfiniteQuery } from "@tanstack/react-query";
import { request } from "../api/client";
import type { Page } from "../api/types";
import { opsKeys } from "../inventory/api";

export interface PosItem {
  variant_id: string;
  product_id: string;
  product_title: string;
  variant_title: string;
  sku: string | null;
  barcode: string | null;
  price_paisa: number;
  track_inventory: boolean;
  available: number | null;
}

export type TenderKind = "cash" | "merchant_qr" | "external";
export interface Tender {
  kind: TenderKind;
  amount_paisa: number;
  reference?: string;
  /** Set by the server for a QR / external tender recorded with its reference (POS-03). */
  confirmation?: "manual_reference";
}

export interface SaleRequest {
  location_id?: string;
  /** POS-04: the paired register of this device (the sale joins its open shift). */
  register_id?: string;
  lines: { variant_id: string; qty: number; discount_paisa?: number }[];
  tenders: Tender[];
  customer?: { phone?: string };
  b2b?: { pan: string; name: string };
}

export interface Sale {
  id: string;
  receipt_number: string;
  status: string;
  channel: "pos";
  order_id: string | null;
  order_number: string | null;
  location_id: string;
  lines: {
    variant_id: string;
    title: string;
    qty: number;
    unit_price_paisa: number;
    line_total_paisa: number;
  }[];
  tenders: Tender[];
  totals: {
    subtotal: number;
    discount: number;
    delivery: number;
    surcharge: number;
    tax: number;
    total: number;
  };
  paid_paisa: number;
  change_paisa: number;
  created_at: string;
}

const s = (orgId: string, storeId: string) =>
  `/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}`;

export const posApi = {
  lookup: (
    orgId: string,
    storeId: string,
    q: { q?: string; barcode?: string; location?: string },
    signal?: AbortSignal,
  ) =>
    request<{ data: PosItem[] }>("GET", `${s(orgId, storeId)}/pos/catalog`, { query: q, signal }),
  sell: (orgId: string, storeId: string, body: SaleRequest, idempotencyKey: string) =>
    request<Sale>("POST", `${s(orgId, storeId)}/pos/sales`, { body, idempotencyKey }),
  sales: (orgId: string, storeId: string, cursor?: string) =>
    request<Page<Sale>>("GET", `${s(orgId, storeId)}/pos/sales`, { query: { cursor, limit: 50 } }),
};

export const useSales = (orgId: string, storeId: string, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: opsKeys.sales(orgId, storeId),
    queryFn: ({ pageParam }) => posApi.sales(orgId, storeId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });

/** Quick-tender amounts (DESIGN_SYSTEM §9.3): exact, then the next round Rs 100 / 500 / 1,000. */
export function quickTenders(totalPaisa: number): number[] {
  const out = [totalPaisa];
  for (const step of [100_00, 500_00, 1000_00]) {
    const up = Math.ceil(totalPaisa / step) * step;
    if (up > totalPaisa && !out.includes(up)) out.push(up);
  }
  return out;
}

/** "3*8901234567890" → { qty: 3, code }; a bare code is qty 1 (DESIGN_SYSTEM §9.3). */
export function parseScan(raw: string): { qty: number; code: string } {
  const m = /^(\d{1,3})\*(.+)$/.exec(raw.trim());
  if (m) return { qty: Math.max(1, Number(m[1])), code: m[2]!.trim() };
  return { qty: 1, code: raw.trim() };
}
